/* redesign-ugc-marquee.js — an endless cycle of the SAME reels.
   ---------------------------------------------------------------------------
   Brandon: "instead of pasting new reels js make sure reels are in a never
   ending cycle being reused."

   WHY NOT THE CSS MARQUEE. The usual trick renders the row twice and animates
   to -50%. For text that is right. Here every card is a <video>, so duplicating
   meant twelve video elements for six clips — twelve decoders, twelve buffers,
   on a page where heavy media already cost this store a conversion collapse.

   WHAT THIS DOES INSTEAD. One node per clip. Each frame the track is nudged
   left; the moment a card's trailing edge crosses the left edge, that SAME node
   is appended to the end of the track and its width is subtracted from the
   offset. Nothing is created, nothing is destroyed, and there is no seam.

   ===========================================================================
   THREE BUGS FIXED 2026-08-20 — read before "optimising" any of this back.

   1. CLIPS FROZE AS THEY RECYCLED.
      A per-card IntersectionObserver paused and REWOUND each clip when it left
      the row, then replayed it on re-entry. Two things went wrong: appending a
      node re-fires the observer immediately (a recycled card reports as "left"
      the instant it is moved), and `currentTime = 0` on a preload="none" video
      that has not buffered drops it back to its poster with nothing to resume.
      Result: a card that recycled once never played again.
      FIX: no per-card observer at all. Six muted 480px clips is not the load
      that needs micromanaging — the SECTION observer already stops everything
      when the carousel is off screen, which is where the real saving was.

   2. HOVER DID NOT PLAY.
      Hover called `video.muted = false`, and a browser will PAUSE a video that
      is unmuted without user activation. `mouseover` is not activation, so the
      hover handler was stopping the very clip it was meant to start.
      FIX: hover always keeps the clip playing, and only unmutes once the page
      has had a real gesture. If play() still rejects, it re-mutes and carries
      on rather than leaving a dead card.

   3. CLICK DID NOT OPEN ANYTHING.
      Click latched sound on in place. Brandon expected it to "pop up and play".
      FIX: click opens a lightbox with that clip, unmuted, with native controls,
      and the wall holds still until it closes.

   4. THE LIGHTBOX PLAYED, UNMUTED, AT FULL VOLUME — AND WAS STILL SILENT.
      Twice reported as "no sound when I click". It was never a JS bug: the
      wall's clips are re-encodes from tools/make-loops.sh, and that script
      passes `-an` to ffmpeg — EVERY rd-ugc-*.mp4/.webm on the CDN has NO
      AUDIO TRACK AT ALL (verified with ffprobe 2026-08-20: streams = h264
      video only). No amount of muted=false can conjure a track that was
      stripped at encode time.
      FIX: each clip now ships a sibling `<basename>-sound.mp4` — the SAME
      already-approved video stream (ffmpeg -c:v copy, so the caption-cropped
      pixels are byte-identical) with the creator's AAC audio muxed back in
      from the raw file. The section exposes it as data-ugcmq-sound; the
      lightbox plays THAT, and only the lightbox — the wall keeps the silent
      files, so the 99% who never click download zero extra bytes.
      And silence is never silent anymore: if unmuted play() is refused we
      show a "Tap for sound" button instead of quietly falling back, and if
      the playing file truly has no audio track (missing -sound asset, or a
      new clip added without one) we say so on screen.
      ⚠️ EVERY NEW CLIP NEEDS ITS -sound.mp4. Mux it, never re-encode video:
        ffmpeg -i assets/rd-ugc-X.mp4 -i <raw-with-audio> -map 0:v -map 1:a \
               -c:v copy -c:a aac -b:a 96k -movflags +faststart -shortest \
               assets/rd-ugc-X-sound.mp4
      (Listen to it first — spoken claims become OUR claims, same as the
      burned-in captions. See the section file's compliance comment.)
   ===========================================================================
*/
(function () {
  'use strict';

  var REDUCED = window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Browsers only allow unmuted playback after the page has been interacted
  // with. Tracked once, globally — see bug 2 above.
  var gestured = false;
  ['pointerdown', 'keydown', 'touchstart'].forEach(function (evt) {
    document.addEventListener(evt, function () { gestured = true; },
                              { once: true, passive: true });
  });

  /* ---------------- lightbox ---------------- */

  var box = null;
  var boxV = null, boxUnmute = null, boxNote = null;
  var boxCard = null;        // card the lightbox is showing, for the fallback
  var boxFellBack = false;   // already retreated to the silent wall sources?
  var boxAudioChecked = false;

  // Chrome exposes decoded audio bytes, Firefox a boolean, Safari audioTracks.
  // Only trustworthy once playback has actually advanced a little.
  function clipHasAudio(v) {
    if (typeof v.webkitAudioDecodedByteCount === 'number') return v.webkitAudioDecodedByteCount > 0;
    if (typeof v.mozHasAudio === 'boolean') return v.mozHasAudio;
    if (v.audioTracks) return v.audioTracks.length > 0;
    return true;   // can't tell — stay quiet rather than nag falsely
  }

  function showNote(text) {
    boxNote.textContent = text;
    boxNote.hidden = false;
  }

  function lightbox() {
    if (box) return box;
    box = document.createElement('div');
    box.className = 'rd-ugcbox';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.hidden = true;
    // The unmute button and the no-audio note are styled INLINE on purpose:
    // redesign-ugc-marquee.css is owned by concurrent work, and these two
    // exist precisely so a sound failure can never again be invisible — they
    // must not depend on a stylesheet that might not know about them yet.
    box.innerHTML =
      '<button type="button" class="rd-ugcbox__close" aria-label="Close">&times;</button>' +
      '<div class="rd-ugcbox__stage"><video class="rd-ugcbox__v" controls playsinline></video>' +
      '<button type="button" class="rd-ugcbox__unmute" hidden ' +
        'style="position:absolute;left:50%;top:40%;transform:translate(-50%,-50%);' +
        'padding:14px 22px;border:0;border-radius:999px;cursor:pointer;' +
        'background:rgba(11,14,26,.85);color:#F5F7FB;font-size:16px;font-weight:600;' +
        'box-shadow:0 4px 24px rgba(0,0,0,.5);z-index:2">' +
        '&#128266; Tap for sound</button>' +
      '<div class="rd-ugcbox__note" role="status" hidden ' +
        'style="position:absolute;left:50%;bottom:14%;transform:translateX(-50%);' +
        'padding:8px 16px;border-radius:999px;background:rgba(11,14,26,.85);' +
        'color:#9DA5BF;font-size:13px;white-space:nowrap;z-index:2"></div>' +
      '</div>';
    document.body.appendChild(box);
    boxV = box.querySelector('.rd-ugcbox__v');
    boxUnmute = box.querySelector('.rd-ugcbox__unmute');
    boxNote = box.querySelector('.rd-ugcbox__note');
    // The stage needs to anchor the overlays even if the CSS doesn't say so.
    box.querySelector('.rd-ugcbox__stage').style.position = 'relative';

    box.addEventListener('click', function (e) {
      // Backdrop or the close button closes; the video itself does not.
      if (e.target === box || e.target.closest('.rd-ugcbox__close')) closeBox();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !box.hidden) closeBox();
    });

    // Tapping the button IS the user activation unmuted playback wants.
    boxUnmute.addEventListener('click', function () {
      boxV.muted = false;
      boxV.volume = 1;
      boxUnmute.hidden = true;
      var p = boxV.play();
      if (p && p.catch) p.catch(function () {});
    });

    // The -sound variant failed to load (missing asset, bad push) — retreat
    // to the silent wall sources so the visitor still gets the video, and SAY
    // there is no sound instead of playing dumb.
    boxV.addEventListener('error', function () {
      if (box.hidden || boxFellBack || !boxCard) return;
      boxFellBack = true;
      var src = boxCard.querySelector('[data-ugcmq-video]');
      if (!src) return;
      boxV.removeAttribute('src');
      boxV.innerHTML = '';
      Array.prototype.forEach.call(src.querySelectorAll('source'), function (s) {
        boxV.appendChild(s.cloneNode(true));
      });
      boxV.load();
      boxV.muted = true;   // these files have no track; unmuted adds nothing
      showNote('Sound isn’t available for this clip');
      var p = boxV.play();
      if (p && p.catch) p.catch(function () {});
    });

    // Once playback has genuinely advanced, verify the file we are playing
    // actually CONTAINS audio. This is the tripwire for bug 4 — if someone
    // wires a new clip without its -sound.mp4, the lightbox says so out loud
    // instead of shipping another silent mystery.
    boxV.addEventListener('timeupdate', function () {
      if (box.hidden || boxAudioChecked || boxV.currentTime < 0.4) return;
      boxAudioChecked = true;
      if (!boxV.muted && !clipHasAudio(boxV)) {
        showNote('This clip has no sound');
      }
    });
    return box;
  }

  var boxOwner = null;   // the wall that opened it, so we can resume the right one

  function openBox(card, wall) {
    var src = card.querySelector('[data-ugcmq-video]');
    if (!src) return;
    var b = lightbox();
    var v = boxV;

    boxCard = card;
    boxFellBack = false;
    boxAudioChecked = false;
    boxUnmute.hidden = true;
    boxNote.hidden = true;

    v.innerHTML = '';
    v.removeAttribute('src');
    // Bug 4: the wall's own files are silent by design (-an). The lightbox
    // plays the -sound sibling, which carries the identical video stream plus
    // the creator's audio. Fall back to the wall <source> list only if that
    // asset errors (see the error handler above).
    var soundSrc = src.getAttribute('data-ugcmq-sound');
    if (soundSrc) {
      v.src = soundSrc;
    } else {
      // Old markup without the attribute — behave, but the timeupdate check
      // will surface the silence.
      Array.prototype.forEach.call(src.querySelectorAll('source'), function (s) {
        v.appendChild(s.cloneNode(true));
      });
    }
    v.poster = src.getAttribute('poster') || '';
    v.load();
    // ⚠️ Do NOT set currentTime here. Assigning it before metadata has loaded
    // throws InvalidStateError, which aborted the rest of this handler — so
    // play() was never reached and the lightbox opened silent. A freshly
    // load()ed video starts at 0 anyway.
    v.muted = false;          // a click IS user activation, so this is allowed
    v.volume = 1;

    b.hidden = false;
    document.body.classList.add('rd-ugcbox-open');
    boxOwner = wall;
    if (wall) wall.setAttribute('data-paused', '');

    var p = v.play();
    if (p && p.catch) p.catch(function () {
      // Autoplay policy refused unmuted playback. Do NOT silently settle for
      // muted — that is exactly the failure Brandon hit twice. Play muted so
      // the frame isn't black, and put recovery ONE tap away, visibly.
      v.muted = true;
      var r = v.play();
      if (r && r.catch) r.catch(function () {});
      boxUnmute.hidden = false;
    });
    b.querySelector('.rd-ugcbox__close').focus();
  }

  function closeBox() {
    if (!box || box.hidden) return;
    var v = boxV;
    v.pause();
    v.innerHTML = '';
    v.removeAttribute('src');
    v.load();                                   // release the decoder
    boxUnmute.hidden = true;
    boxNote.hidden = true;
    boxCard = null;
    box.hidden = true;
    document.body.classList.remove('rd-ugcbox-open');
    if (boxOwner) boxOwner.removeAttribute('data-paused');
    boxOwner = null;
  }

  /* ---------------- the wall ---------------- */

  function initWall(wall) {
    if (wall.dataset.ugcmqReady) return;
    wall.dataset.ugcmqReady = '1';

    var track = wall.querySelector('[data-ugcmq-track]');
    var seq   = wall.querySelector('.rd-ugcmq__seq');
    if (!track || !seq) return;

    /* STILL — this wall does not scroll.
       Two independent reasons, handled identically:
         1. prefers-reduced-motion (global, honoured everywhere)
         2. data-ugcmq-static — set by the section when it has too few clips
            to fill a row. A marquee needs enough distinct cards that the
            recycle is invisible; with one or two, fill() clones the SAME
            clip three or four times and the row reads as a rendering bug
            rather than as a wall. That case is now a static, centred row.
       Everything downstream already knows how to behave when nothing moves —
       the CSS has carried a reduced-motion variant since the wall shipped —
       so this reuses that path instead of adding a second one. */
    var STILL = REDUCED || wall.hasAttribute('data-ugcmq-static');

    var offset = 0, running = false, lastTs = 0, onScreen = true;
    var soundCard = null;

    function videos() { return seq.querySelectorAll('[data-ugcmq-video]'); }

    function gap() {
      var cs = getComputedStyle(seq);
      return parseFloat(cs.columnGap || cs.gap) || 0;
    }

    function speed() {
      var secs = parseFloat(getComputedStyle(wall).getPropertyValue('--rd-ugcmq-speed')) || 45;
      return (seq.scrollWidth || 1) / secs;
    }

    function playAll() {
      // Reduced motion: the CSS turns the wall into a static swipeable row and
      // documents that "the JS leaves every clip paused on its poster" — so
      // honour that. Hover and the lightbox still play, because those are
      // user-initiated.
      if (REDUCED) return;
      Array.prototype.forEach.call(videos(), function (v) {
        if (v.paused) {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        }
      });
    }

    function pauseAll() {
      // Pause only. NEVER rewind — see bug 1.
      Array.prototype.forEach.call(videos(), function (v) { v.pause(); });
    }

    /* ---- fill: only if the row is genuinely shorter than the viewport ----
       ⚠️ FIXED 2026-08-20. This used to clone until scrollWidth reached
       1.6 x viewport, with a guard of 24 — on a 1710px screen that cloned
       ~24 cards, so almost everything sliding past was a CLONE. Clones were
       poster-only and the click handler skipped them, which is exactly what
       "frozen and you cannot click on them" was.

       Because the row RECYCLES, it does not need to be 1.6 screens wide. It
       only needs to be wider than the viewport plus the card currently being
       moved to the back — otherwise a gap opens at the right edge. That is
       one or two clones at most, usually none.

       And clones are now REAL cards: same <video>, same handlers. The browser
       serves the second copy from cache, so an extra clone costs a decoder,
       not a download — and a visitor can click it like any other. */
    function fill() {
      // STILL, not REDUCED: a static row must never clone. Cloning is only
      // ever about hiding a gap at the right edge of a MOVING row.
      if (STILL) return;
      var originals = Array.prototype.slice.call(seq.children).filter(function (c) {
        return !c.dataset.ugcmqClone;
      });
      if (!originals.length) return;

      var widest = 0;
      originals.forEach(function (c) { widest = Math.max(widest, c.offsetWidth); });
      var needed = wall.clientWidth + widest + gap() * 2;

      // Drop stale clones first so a resize down does not leave a long tail.
      Array.prototype.slice.call(seq.children).forEach(function (c) {
        if (c.dataset.ugcmqClone) c.remove();
      });

      var i = 0;
      while (seq.scrollWidth < needed && i < originals.length * 2) {
        var clone = originals[i % originals.length].cloneNode(true);
        clone.dataset.ugcmqClone = '1';
        // Duplicated links must not be tab stops, but everything else about a
        // clone behaves like the original — including play and click.
        Array.prototype.forEach.call(clone.querySelectorAll('a'), function (a) {
          a.setAttribute('tabindex', '-1');
        });
        seq.appendChild(clone);
        i++;
      }
      playAll();
    }

    /* ---- the cycle ---- */
    function step(ts) {
      if (!running) return;
      var dt = lastTs ? Math.min((ts - lastTs) / 1000, 0.05) : 0;
      lastTs = ts;

      if (!wall.hasAttribute('data-paused')) {
        offset += speed() * dt;
        var first = seq.firstElementChild;
        // `while`, not `if`: a restored background tab can owe several cards at
        // once, and one per frame would visibly stutter catching up.
        while (first && offset >= first.offsetWidth + gap()) {
          offset -= first.offsetWidth + gap();
          seq.appendChild(first);          // same node, moved — never recreated
          first = seq.firstElementChild;
        }
        track.style.transform = 'translate3d(' + (-offset) + 'px,0,0)';
      }
      requestAnimationFrame(step);
    }

    function start() {
      if (running || STILL) return;
      running = true; lastTs = 0;
      requestAnimationFrame(step);
    }
    function stop() { running = false; }

    /* ---- section visibility drives BOTH the cycle and playback ---- */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          onScreen = e.isIntersecting;
          if (onScreen) { playAll(); start(); }
          else          { pauseAll(); stop(); }
        });
      }, { threshold: 0.05 }).observe(wall);
    } else {
      playAll();
    }

    /* ---- sound follows the pointer (once the page has been interacted with) ---- */
    function unmute(card) {
      var v = card.querySelector('[data-ugcmq-video]');
      if (!v) return;
      if (soundCard && soundCard !== card) mute(soundCard);
      // Keep it PLAYING regardless; only attempt sound when allowed.
      var p0 = v.play(); if (p0 && p0.catch) p0.catch(function () {});
      if (!gestured) return;                  // muted-but-playing is the win
      v.muted = false;
      card.setAttribute('data-sound-on', '');
      soundCard = card;
      var p = v.play();
      if (p && p.catch) p.catch(function () {
        // Policy refused — go back to muted rather than leaving it stalled.
        v.muted = true;
        card.removeAttribute('data-sound-on');
        soundCard = null;
        var r = v.play(); if (r && r.catch) r.catch(function () {});
      });
    }

    function mute(card) {
      if (!card) return;
      var v = card.querySelector('[data-ugcmq-video]');
      if (v) {
        v.muted = true;
        if (onScreen && v.paused) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      }
      card.removeAttribute('data-sound-on');
      if (soundCard === card) soundCard = null;
    }

    var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;

    // Delegated: cards move around the DOM as they recycle.
    if (canHover) {
      seq.addEventListener('mouseover', function (e) {
        var card = e.target.closest('.rd-ugcmq__card');
        if (!card || card === soundCard) return;
        unmute(card);
      });
      seq.addEventListener('mouseout', function (e) {
        var card = e.target.closest('.rd-ugcmq__card');
        if (!card || card !== soundCard) return;
        if (card.contains(e.relatedTarget)) return;   // still inside the card
        mute(card);
      });
    }

    /* ---- click opens the lightbox ---- */
    seq.addEventListener('click', function (e) {
      if (e.target.closest('a')) return;               // links still navigate
      var card = e.target.closest('.rd-ugcmq__card');
      if (!card) return;
      mute(soundCard);
      openBox(card, wall);
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { pauseAll(); stop(); }
      else if (onScreen)   { playAll(); start(); }
    });

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(fill, 200);
    });

    fill();
    playAll();
    start();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ugcmq]'), initWall);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
